import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createMultilingualString,
  updateMultilingualString,
  fetchMultilingualStrings,
  fetchAvailableLanguages,
} from "../../redux/slices/multilingualStringSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useToast } from "../../components/ui/use-toast";
import { Plus, X, Copy, Zap } from "lucide-react";

const SimpleMultilingualStringDialog = ({
  open,
  onClose,
  multilingualString,
}) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { availableLanguages } = useSelector(
    (state) => state.multilingualString
  );

  const [formData, setFormData] = useState({
    key: "",
    translations: {},
    category: "",
    description: "",
  });

  const [selectedLanguages, setSelectedLanguages] = useState(["en"]);

  useEffect(() => {
    if (open) {
      dispatch(fetchAvailableLanguages());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (multilingualString) {
      setFormData({
        key: multilingualString.key || "",
        translations: multilingualString.translations || {},
        category: multilingualString.category || "",
        description: multilingualString.description || "",
      });
      setSelectedLanguages(Object.keys(multilingualString.translations || {}));
    } else {
      setFormData({
        key: "",
        translations: {},
        category: "",
        description: "",
      });
      setSelectedLanguages(["en"]);
    }
  }, [multilingualString]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTranslationChange = (language, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [language]: value,
      },
    }));
  };

  const addLanguage = (language) => {
    if (!selectedLanguages.includes(language)) {
      setSelectedLanguages((prev) => [...prev, language]);
    }
  };

  const removeLanguage = (language) => {
    if (selectedLanguages.length > 1) {
      setSelectedLanguages((prev) => prev.filter((lang) => lang !== language));
      setFormData((prev) => {
        const newTranslations = { ...prev.translations };
        delete newTranslations[language];
        return {
          ...prev,
          translations: newTranslations,
        };
      });
    }
  };

  const addAllLanguages = () => {
    setSelectedLanguages([...availableLanguages]);
  };

  const copyTranslation = (fromLang, toLang) => {
    if (formData.translations[fromLang]) {
      setFormData((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [toLang]: prev.translations[fromLang],
        },
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.key.trim()) {
      toast({
        title: "Error",
        description: "String key is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.category.trim()) {
      toast({
        title: "Error",
        description: "Category is required",
        variant: "destructive",
      });
      return;
    }

    if (Object.keys(formData.translations).length === 0) {
      toast({
        title: "Error",
        description: "At least one translation is required",
        variant: "destructive",
      });
      return;
    }

    // Check if all selected languages have translations
    const missingTranslations = selectedLanguages.filter(
      (lang) => !formData.translations[lang]?.trim()
    );

    if (missingTranslations.length > 0) {
      toast({
        title: "Error",
        description: `Please provide translations for: ${missingTranslations.join(
          ", "
        )}`,
        variant: "destructive",
      });
      return;
    }

    try {
      if (multilingualString) {
        await dispatch(
          updateMultilingualString({
            key: multilingualString.key,
            data: formData,
          })
        ).unwrap();
        toast({
          title: "Success",
          description: "Multilingual string updated successfully",
        });
      } else {
        await dispatch(createMultilingualString(formData)).unwrap();
        toast({
          title: "Success",
          description: "Multilingual string created successfully",
        });
      }

      dispatch(fetchMultilingualStrings());
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error || "Failed to save multilingual string",
        variant: "destructive",
      });
    }
  };

  const getLanguageName = (code) => {
    const languageNames = {
      en: "English",
      ar: "العربية",
      ur: "اردو",
      fr: "Français",
      es: "Español",
    };
    return languageNames[code] || code.toUpperCase();
  };

  const availableLanguagesToAdd = availableLanguages.filter(
    (lang) => !selectedLanguages.includes(lang)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {multilingualString ? "Edit String" : "Add New String"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="key">String Key *</Label>
              <Input
                id="key"
                value={formData.key}
                onChange={(e) => handleInputChange("key", e.target.value)}
                placeholder="e.g., welcome_message"
                disabled={!!multilingualString}
              />
              <p className="text-sm text-muted-foreground">
                Unique identifier for this string
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => handleInputChange("category", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="common">Common</SelectItem>
                  <SelectItem value="auth">Authentication</SelectItem>
                  <SelectItem value="navigation">Navigation</SelectItem>
                  <SelectItem value="quiz">Quiz</SelectItem>
                  <SelectItem value="profile">Profile</SelectItem>
                  <SelectItem value="settings">Settings</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Brief description of this string's purpose"
              rows={2}
            />
          </div>

          {/* Language Management */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Languages</Label>
              <div className="flex gap-2">
                {availableLanguagesToAdd.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addAllLanguages}
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    Add All
                  </Button>
                )}
                {availableLanguagesToAdd.length > 0 && (
                  <Select onValueChange={addLanguage}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Add language" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableLanguagesToAdd.map((lang) => (
                        <SelectItem key={lang} value={lang}>
                          {getLanguageName(lang)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Translation Inputs */}
            <div className="grid gap-4">
              {selectedLanguages.map((language) => (
                <div key={language} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-base font-medium">
                      {getLanguageName(language)} ({language.toUpperCase()})
                    </Label>
                    <div className="flex gap-2">
                      {selectedLanguages.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLanguage(language)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      {selectedLanguages.filter((l) => l !== language).length >
                        0 && (
                        <Select
                          onValueChange={(fromLang) =>
                            copyTranslation(fromLang, language)
                          }
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue placeholder="Copy from" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedLanguages
                              .filter((l) => l !== language)
                              .map((lang) => (
                                <SelectItem key={lang} value={lang}>
                                  {getLanguageName(lang)}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  <Textarea
                    value={formData.translations[language] || ""}
                    onChange={(e) =>
                      handleTranslationChange(language, e.target.value)
                    }
                    placeholder={`Enter translation in ${getLanguageName(
                      language
                    )}`}
                    rows={2}
                    required
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {multilingualString ? "Update" : "Create"} String
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SimpleMultilingualStringDialog;
